import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import DialogLoading from '../../components/feedback/DialogLoading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import InputInfo from '../../components/ui/InputInfo';
import InputPhoneNumber from '../../components/ui/InputPhoneNumber';
import SelectInfo from '../../components/ui/SelectInfo';
import { userStatus } from '../../constants/userStatus';
import { useGetCompany, useUpdateCompany } from '../../hooks/useCompany';
import { companySchema } from '../../schemas/company';
import { useDialogStore } from '../../stores/dialogStore';

const EditCompanyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loadingDialog, setLoadingDialog] = useState(false);
  const openDialog = useDialogStore((state) => state.openDialog);

  const { data: companyData, isLoading: isLoadingCompany } = useGetCompany({ id });
  const { mutateAsync: updateCompany } = useUpdateCompany();

  const defaultValues = useMemo(() => {
    if (isLoadingCompany) return {};

    if (!_.isEmpty(companyData)) {
      const data = companyData?.Data?.data[0];

      return {
        companyName: data.company_name,
        fname: data.people_name
          .trim()
          .split(' ')
          .filter((item) => item)[0],
        lname: data.people_name
          .trim()
          .split(' ')
          .filter((item) => item)[1],
        phoneNumber: data.phonenumber,
        state: data.state,
      };
    }

    return {};
  }, [companyData, isLoadingCompany]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: defaultValues,
    validationSchema: companySchema,
    onSubmit: async (values) => {
      try {
        setLoadingDialog(true);

        const dto = {
          company_name: values.companyName,
          people_name: `${values.fname} ${values.lname}`,
          phonenumber: values.phoneNumber,
          state: values.state,
        };

        const response = await updateCompany({ params: { id }, dto });

        if (response.Code === '20100') {
          openDialog({
            title: 'สำเร็จ',
            message: 'แก้ไขข้อมูลบริษัทสำเร็จ',
            variant: 'success',
            onCloseCallback: () => {
              navigate(`/admin/${id}`);
            },
          });
        }
      } catch (err) {
        console.error(err);
        openDialog({
          title: 'เกิดข้อผิดพลาด',
          message: 'แก้ไขข้อมูลบริษัทไม่สำเร็จ',
          variant: 'error',
        });
      } finally {
        setLoadingDialog(false);
      }
    },
  });

  return (
    <Box
      sx={{
        width: '100%',
        padding: '6rem 2rem 40px',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <Typography
        sx={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#002B38',
          textAlign: 'center',
          marginBottom: '40px',
        }}
      >
        แก้ไขข้อมูลร้านค้า
      </Typography>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          maxWidth: '768px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {/* Company Name */}
        <Box>
          <InputInfo
            id="companyName"
            name="companyName"
            text="ชื่อร้านค้า"
            placeholder="ชื่อร้านค้า"
            value={formik.values.companyName}
            onChange={formik.handleChange}
          />
          {formik.touched.companyName && formik.errors.companyName && (
            <ErrorMessage>{formik.errors.companyName}</ErrorMessage>
          )}
        </Box>

        {/* First name */}
        <Box>
          <InputInfo
            id="fname"
            name="fname"
            text="ชื่อ"
            placeholder="ชื่อ"
            value={formik.values.fname}
            onChange={formik.handleChange}
          />
          {formik.touched.fname && formik.errors.fname && (
            <ErrorMessage>{formik.errors.fname}</ErrorMessage>
          )}
        </Box>

        {/* Last name */}
        <Box>
          <InputInfo
            id="lname"
            name="lname"
            text="นามสกุล"
            placeholder="นามสกุล"
            value={formik.values.lname}
            onChange={formik.handleChange}
          />
          {formik.touched.lname && formik.errors.lname && (
            <ErrorMessage>{formik.errors.lname}</ErrorMessage>
          )}
        </Box>

        {/* Phone */}
        <Box>
          <InputPhoneNumber
            id="phoneNumber"
            name="phoneNumber"
            text="เบอร์โทรศัพท์"
            placeholder="เบอร์โทรศัพท์"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <ErrorMessage>{formik.errors.phoneNumber}</ErrorMessage>
          )}
        </Box>

        {/* Status */}
        <Box>
          <SelectInfo
            id="state"
            name="state"
            label="สถานะ"
            listItems={userStatus}
            value={formik.values.state}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.state && formik.errors.state && (
            <ErrorMessage>{formik.errors.state}</ErrorMessage>
          )}
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 0',
            marginTop: '50px',
          }}
        >
          <Button
            type="button"
            sx={{
              width: '250px',
              height: '50px',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#004C60',
              fontSize: '1.25rem',

              '&:hover': {
                backgroundColor: 'white',
                color: '#004C60',
                opacity: '.85',
              },
            }}
            onClick={() => navigate(`/admin/${id}`)}
          >
            ย้อนกลับ
          </Button>
          <Button
            type="submit"
            sx={{
              width: '250px',
              height: '50px',
              borderRadius: '8px',
              backgroundColor: '#E1000C',
              color: 'white',
              fontSize: '1.25rem',

              '&:hover': {
                backgroundColor: '#E1000C',
                color: 'white',
                opacity: '.85',
              },
            }}
          >
            บันทึก
          </Button>
        </Box>
      </Box>

      <DialogLoading open={loadingDialog} message="กำลังอัพเดตข้อมูลร้านค้า" />
    </Box>
  );
};

export default EditCompanyPage;
